import { z } from 'zod';

// ---------------------------------------------------------------------------
// GET /v1/realizations?wallpaper_id=&limit=&offset=
//
// Public read facade over `wallpaper_realizations` (RLS blocks anon from
// unapproved rows; the api/ service-role client bypasses RLS, so the query
// itself MUST filter approved=true — see queries/realizations.ts).
//
// Additive to the frozen /v1/ contract (2026-05-27) — new endpoint, no
// changes to existing routes.
//
// IDs are BIGINT in the DB → modelled as positive ints (NOT UUID).
// Results ordered by sort ASC, id ASC (stable pagination, matches the DB
// index (wallpaper_id, approved, sort) from the T4 migration).
// ---------------------------------------------------------------------------

export const RealizationsRequestSchema = z.object({
  wallpaper_id: z.coerce.number().int().positive(),
  limit: z.coerce.number().int().min(1).max(100).default(24),
  offset: z.coerce.number().int().min(0).default(0),
});
export type RealizationsRequest = z.infer<typeof RealizationsRequestSchema>;

export const RealizationItemSchema = z.object({
  id: z.number().int().positive(),
  wallpaper_id: z.number().int().positive(),
  /** Base public object URL (no imgproxy transform) — same contract as InteriorItem.image_url. */
  image_url: z.string().url(),
  /** Bucket-relative path (wandero bucket) — for T6 to build responsive URLs via wpImageUrl/interiorImageUrl. */
  storage_path: z.string(),
  /** Honest caption (plain text, never HTML) — null falls back to a generic i18n label in T6. */
  caption: z.string().nullable(),
  rating: z.number().int().nullable(),
  review_text: z.string().nullable(),
  author_name: z.string().nullable(),
  sort: z.number().int(),
});
export type RealizationItem = z.infer<typeof RealizationItemSchema>;

export const RealizationsResponseSchema = z.object({
  items: z.array(RealizationItemSchema),
  total: z.number().int().nonnegative(),
});
export type RealizationsResponse = z.infer<typeof RealizationsResponseSchema>;
